import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApp } from '@/context/AppContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { pushSupported, permissionState, currentSubscription, enablePush, disablePush, sendTestPush } from '@/lib/push';
import { Bell, BellOff, Loader2, Send, Moon } from 'lucide-react';

// Enable/disable browser push (works even when the app tab is closed on
// desktop + Android; iOS needs the app installed as a PWA).
export function PushToggle({ compact = false }) {
  const supported = pushSupported();
  const { user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [quiet, setQuiet] = useState({ enabled: false, start: '22:00', end: '07:00' });

  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      try {
        const d = (await api.get('/push/quiet')).data.data;
        setQuiet({ enabled: d.enabled, start: d.start, end: d.end });
      } catch (e) { /* ignore */ }
    })();
  }, [isOwner]);

  const saveQuiet = async (next) => {
    setQuiet(next);
    try {
      await api.put('/push/quiet', { ...next, tz_offset: -new Date().getTimezoneOffset() });
      toast.success('Quiet hours updated');
    } catch (e) { toast.error('Could not save quiet hours'); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supported) { setReady(true); return; }
      try {
        const sub = await currentSubscription();
        if (!cancelled) setOn(!!sub && permissionState() === 'granted');
      } catch (e) { /* ignore */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); toast.success('Browser alerts turned off'); }
      else { await enablePush(); setOn(true); toast.success('Browser alerts turned on'); }
    } catch (e) { toast.error(e.message || 'Could not update alerts'); }
    finally { setBusy(false); }
  };

  const test = async () => {
    try { await sendTestPush(); toast.message('Test sent', { description: 'It should pop up shortly.' }); }
    catch (e) { toast.error('Could not send test'); }
  };

  if (!supported) {
    return compact ? null : (
      <div className="rounded-xl border border-border bg-card p-3 text-xs text-muted-foreground" data-testid="push-unsupported">
        Browser notifications aren&apos;t supported here. On iPhone, add this app to your Home Screen to enable them.
      </div>
    );
  }

  if (compact) {
    return (
      <Button variant={on ? 'secondary' : 'default'} size="sm" className="h-9 rounded-lg" onClick={toggle} disabled={busy || !ready} data-testid="push-toggle-compact">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : on ? <BellOff className="mr-1 h-4 w-4" /> : <Bell className="mr-1 h-4 w-4" />}
        {on ? 'Alerts on' : 'Enable alerts'}
      </Button>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sc-card" data-testid="push-panel">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Browser notifications</p>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${on ? 'bg-emerald-500/15 text-emerald-600' : 'bg-secondary text-muted-foreground'}`}>{on ? 'On' : 'Off'}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Get a popup for every new sale and your daily due list — even when this tab is closed (desktop &amp; Android; on iPhone, add to Home Screen first).</p>
      <div className="mt-3 flex gap-2">
        <Button className="h-11 flex-1 rounded-xl" onClick={toggle} disabled={busy || !ready} data-testid="push-toggle">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : on ? <BellOff className="mr-1 h-4 w-4" /> : <Bell className="mr-1 h-4 w-4" />}
          {on ? 'Turn off' : 'Turn on'} browser alerts
        </Button>
        {on && <Button variant="secondary" className="h-11 rounded-xl" onClick={test} data-testid="push-test"><Send className="mr-1 h-4 w-4" />Test</Button>}
      </div>
      {isOwner && (
        <div className="mt-3 border-t border-border pt-3" data-testid="quiet-hours">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Moon className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Quiet hours</span></div>
            <Switch checked={quiet.enabled} onCheckedChange={(v) => saveQuiet({ ...quiet, enabled: v })} data-testid="quiet-enabled" />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Mute push during these hours so nights stay silent (in-app history still updates).</p>
          {quiet.enabled && (
            <div className="mt-2 flex items-end gap-2">
              <div><Label className="text-[11px] text-muted-foreground">From</Label><Input type="time" value={quiet.start} onChange={(e) => setQuiet({ ...quiet, start: e.target.value })} onBlur={() => saveQuiet(quiet)} className="mt-1 h-10 rounded-lg" data-testid="quiet-start" /></div>
              <div><Label className="text-[11px] text-muted-foreground">To</Label><Input type="time" value={quiet.end} onChange={(e) => setQuiet({ ...quiet, end: e.target.value })} onBlur={() => saveQuiet(quiet)} className="mt-1 h-10 rounded-lg" data-testid="quiet-end" /></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
